import React, { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { useAdminAuth } from "@/contexts/AdminAuthContext";
import { Lock } from "lucide-react";

export default function AdminLogin() {
  const { login, user, loading } = useAdminAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center text-zinc-500 text-xs tracking-[0.3em] uppercase">
        Ověřuji…
      </div>
    );
  }

  if (user) return <Navigate to="/admin" replace />;

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error("Zadejte e-mail a heslo.");
      return;
    }
    setSubmitting(true);
    try {
      await login(email, password);
      toast.success("Přihlášeno.");
      navigate("/admin", { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Přihlášení se nezdařilo.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white flex items-center justify-center px-6 relative">
      <div className="grain-overlay" aria-hidden="true" />
      <div className="absolute top-8 left-8">
        <a href="/" className="flex items-center gap-3 group">
          <div className="w-9 h-9 border border-[#D4AF37]/50 flex items-center justify-center">
            <span className="font-display text-[#D4AF37] text-xl leading-none">C</span>
          </div>
              <div className="leading-none">
                  <div className="font-display text-xl tracking-wide text-white">CarSpa</div>
                  <div className="text-[10px] tracking-[0.35em] uppercase text-[#D4AF37]/80">Admin</div>
              </div>
        </a>
      </div>

      <form
        onSubmit={submit}
        data-testid="admin-login-form"
        className="gold-border p-10 w-full max-w-md"
      >
        <Lock className="text-[#D4AF37] mb-6" size={28} strokeWidth={1} />
        <div className="text-[11px] tracking-[0.4em] uppercase text-[#D4AF37] mb-3">
          Admin přístup
        </div>
        <h1 className="font-display text-3xl md:text-4xl text-white font-light mb-8">
          Přihlášení
        </h1>

        <label className="block mb-5">
          <span className="block text-[10px] tracking-[0.35em] uppercase text-zinc-400 mb-2">E-mail</span>
          <input
            data-testid="admin-email-input"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoComplete="username"
            className="w-full bg-transparent border-0 border-b border-white/15 focus:border-[#D4AF37] focus:outline-none py-3 text-white placeholder:text-zinc-600"
            placeholder="admin@carspa.cz"
          />
        </label>

        <label className="block mb-8">
          <span className="block text-[10px] tracking-[0.35em] uppercase text-zinc-400 mb-2">Heslo</span>
          <input
            data-testid="admin-password-input"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            autoComplete="current-password"
            className="w-full bg-transparent border-0 border-b border-white/15 focus:border-[#D4AF37] focus:outline-none py-3 text-white placeholder:text-zinc-600"
            placeholder="••••••••"
          />
        </label>

        <button
          type="submit"
          disabled={submitting}
          data-testid="admin-login-submit"
          className="btn-gold w-full bg-[#D4AF37] hover:bg-[#F0C846] disabled:opacity-50 text-black px-8 py-4 text-xs tracking-[0.3em] uppercase font-semibold transition-colors"
        >
          {submitting ? "Přihlašuji…" : "Přihlásit se"}
        </button>

        <p className="text-[11px] tracking-[0.25em] uppercase text-zinc-600 text-center mt-6">
          Návrat na stránku &nbsp;·&nbsp; <a href="/" className="hover:text-[#D4AF37]">carspa.cz</a>
        </p>
      </form>
    </div>
  );
}
